import { useState } from 'react';
import { MissionOutcome } from '@/shared/api/generated/model';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';

/**
 * Closes a mission with one of §23.3's seven outcomes.
 *
 * ⚠️ The outcome has no default, deliberately.
 *
 * It feeds the final report and the statistics, and §23.3 calls the list closed — so this is the one
 * field in the panel where a pre-selected value would be actively harmful. A dialog opening on "found
 * alive" invites an operator under time pressure to confirm the best outcome for a search that ended
 * some other way, and the statistic is then wrong permanently.
 *
 * This is also the case that justified bringing `select` back: seven mutually exclusive values that
 * must be read before one is chosen is a dropdown, where the board's four filters are chips.
 */
/**
 * Mirrors `Mission.Allows` on the server, and mirrors its restraint too.
 *
 * ⚠️ Only the three whose Arabic names the wrong subject are withheld: «تمّ إنقاذ العالق» from a
 * missing-person search, «عُثر على المفقود سالماً / متوفّى» from a stranded rescue. The other four stay
 * on every list because each has a real reading for both — a driver who freed the vehicle before
 * anybody arrived is «عاد من تلقاء نفسه». §23.3's list is the client's; narrowing it further from the
 * wording alone would be inventing scope.
 */
function outcomesFor(missionType: string): MissionOutcome[] {
  return Object.values(MissionOutcome).filter((outcome) => {
    if (outcome === 'StrandedRescued') return missionType === 'Stranded';
    if (outcome === 'FoundAlive' || outcome === 'FoundDeceased') return missionType === 'Missing';
    return true;
  });
}

export function CloseMissionDialog({
  missionTitle,
  missionType,
  open,
  onOpenChange,
  onConfirm,
  busy,
  serverError,
}: {
  missionTitle: string;

  /**
   * ⚠️ **Which of §23.3's seven this search can honestly have ended with.**
   *
   * The list was rendered whole for every mission, so a missing-person search could be closed as
   * «تمّ إنقاذ العالق» and a stranded rescue as «عُثر على المفقود سالماً» — and the outcome is printed
   * on the mission report, on the certificates of thanks and counted in the statistics permanently.
   * `Mission.Allows` is the authority; this only stops the operator reaching for it.
   */
  missionType: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (outcome: MissionOutcome, summary: string | null) => void;
  busy: boolean;
  /**
   * ⚠️ Rendered inside the dialog, not behind it.
   *
   * The dialog stays open when the server refuses, and Radix marks everything outside a modal
   * `aria-hidden` while covering it with the overlay — so an error shown in the row was invisible to a
   * screen reader and hidden behind the overlay for everyone else. A refused closure looked like a
   * dialog that had simply stopped responding.
   */
  serverError?: string | null;
}) {
  const { t } = useLocale();
  const [outcome, setOutcome] = useState<MissionOutcome | ''>('');
  const [summary, setSummary] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setOutcome('');
    setSummary('');
    setError(null);
  };

  const submit = () => {
    if (!outcome) {
      setError(t.missions.outcomeRequired);
      return;
    }

    // Null rather than an empty string: the summary is optional, and "" would be stored as a summary
    // that says nothing instead of as an absent one.
    onConfirm(outcome, summary.trim() || null);
    reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset();
        }

        onOpenChange(next);
      }}
    >
      <DialogContent closeLabel={t.missions.closeAria}>
        <DialogHeader>
          <DialogTitle>{t.missions.closeDialogTitle}</DialogTitle>
          <DialogDescription>{t.missions.closeDialogLede}</DialogDescription>
        </DialogHeader>

        {/* Named inside the dialog: the overlay hides the board, and closing the wrong search is not
            recoverable — a closed mission completes its members and stops their tracking (§6). */}
        <p className="font-semibold">{missionTitle}</p>

        <Field label={t.missions.outcomeLabel} htmlFor="close-outcome">
          <Select
            value={outcome}
            onValueChange={(value) => {
              setOutcome(value as MissionOutcome);
              setError(null);
            }}
          >
            <SelectTrigger id="close-outcome" className="w-full">
              <SelectValue placeholder={t.missions.outcomePlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {outcomesFor(missionType).map((value) => (
                <SelectItem key={value} value={value}>
                  {t.missionOutcome[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label={t.missions.summaryLabel} htmlFor="close-summary">
          <textarea
            id="close-summary"
            rows={3}
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
            placeholder={t.missions.summaryPlaceholder}
            className="w-full rounded-md border border-border bg-surface-elevated px-3 py-2 text-start outline-none focus:border-primary"
          />
        </Field>

        {error ?? serverError ? (
          <p role="alert" className="text-sm text-danger">
            {error ?? serverError}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t.missions.cancel}
          </Button>
          <Button variant="danger" disabled={busy} onClick={submit}>
            {t.missions.confirmClose}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
